// ─────────────────────────────────────────────────────────────────────────────
// Shared, mutable runtime state for the 3D experience layer.
// Written by DOM listeners / the layout tick in Experience3D, read every frame
// inside the R3F scene. Kept outside React state on purpose so pointer/scroll
// updates never trigger re-renders.
// ─────────────────────────────────────────────────────────────────────────────
import type { Variant } from "./android/rig";
import type { PropName } from "./android/props";

/** Where an android should stand, measured from a [data-actor] slot (CSS px). */
export interface ActorTarget {
  /** Identity of the slot; a change makes the android dematerialize + reappear. */
  key: string;
  /** Slot centre x and bottom y (the feet), and slot height. */
  x: number;
  y: number;
  h: number;
  pose?: string;
  /** Base facing: -1 turned to screen-left … 1 turned to screen-right. */
  face: number;
  /** Section scroll progress 0..1 and "turn" scrub flag. */
  p: number;
  turn: boolean;
}

export interface PropTarget {
  key: string;
  name: PropName;
  /** Slot centre and size (CSS px). */
  x: number;
  y: number;
  w: number;
  h: number;
  p: number;
  step: number;
}

export interface FxState {
  /** Pointer in normalized device coords (-1..1), y up. */
  pointer: { x: number; y: number };
  /** Focused form field centre in NDC, or null when nothing is focused. */
  focus: { x: number; y: number } | null;
  /** Raw window.scrollY and page scroll progress 0..1. */
  scrollY: number;
  scroll: number;
  /** Android slots in the section crossing the viewport centre. */
  actors: Record<Variant, ActorTarget | null>;
  props: PropTarget[];
  /** The page has its own robot acts, so no docked companion is shown. */
  hasStages: boolean;
  /** Site theme; props / scan rings switch blending + colour with it. */
  theme: "light" | "dark";
  /**
   * Width (px) of the empty gutter left of the visible form, or 0 when no form
   * is on screen. A wide gutter puts the companion into "assistant" pose.
   */
  formGutter: number;
  /** Left inset (px) taken by fixed chrome, e.g. the desktop side rail. */
  insetLeft: number;
  /** Bottom inset (px) taken by fixed chrome, e.g. the mobile dock. */
  insetBottom: number;
  /** performance.now() timestamps used to trigger one-shot gestures. */
  waveAt: number;
  typingAt: number;
  arrivedAt: number;
}

export const fx: FxState = {
  pointer: { x: 0.3, y: 0 },
  focus: null,
  scrollY: 0,
  scroll: 0,
  actors: { male: null, female: null },
  props: [],
  hasStages: false,
  theme: "dark",
  formGutter: 0,
  insetLeft: 0,
  insetBottom: 0,
  waveAt: 0,
  typingAt: 0,
  arrivedAt: 0,
};

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const supportsWebGL = (() => {
  let cached: boolean | null = null;
  return () => {
    if (cached !== null) return cached;
    try {
      const c = document.createElement("canvas");
      cached = !!(c.getContext("webgl2") || c.getContext("webgl"));
    } catch {
      cached = false;
    }
    return cached;
  };
})();
